import { supabase } from '../firebase/config';

export interface AdminUserRecord {
  id: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  accountType: 'customer' | 'vendor' | 'admin';
  isActive: boolean;
  isVerified: boolean;
  createdAt: Date;
}

class AdminService {
  async getAllUsers(): Promise<AdminUserRecord[]> {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, display_name, first_name, last_name, account_type, is_active, is_verified, created_at')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((row) => ({
      id: row.id,
      email: row.email,
      displayName: row.display_name || row.email,
      firstName: row.first_name || '',
      lastName: row.last_name || '',
      accountType: row.account_type,
      isActive: row.is_active,
      isVerified: row.is_verified,
      createdAt: new Date(row.created_at),
    }));
  }

  private async getAccessToken(): Promise<string> {
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session?.access_token) {
      throw new Error('Not authenticated');
    }
    return data.session.access_token;
  }

  async suspendUser(userId: string): Promise<void> {
    await this.runAdminAction('suspend', userId);
  }

  async unsuspendUser(userId: string): Promise<void> {
    await this.runAdminAction('unsuspend', userId);
  }

  async deleteUser(userId: string): Promise<void> {
    await this.runAdminAction('delete', userId);
  }

  private async runAdminAction(action: 'suspend' | 'unsuspend' | 'delete', userId: string) {
    const token = await this.getAccessToken();
    const response = await fetch('/api/admin', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ action, userId }),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.error || `Failed to ${action} user`);
    }
  }
}

export const adminService = new AdminService();
